import { useEffect, lazy, Suspense } from 'react';

import axios from 'axios';
import './App.css';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { ConfirmProvider } from './components/ConfirmDialog';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,        // 30sn fresh
      gcTime: 5 * 60 * 1000,       // 5dk cache
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
import { useToast } from './hooks/useToast';
import ToastContainer from './components/ToastContainer';
import ErrorBoundary from './components/ErrorBoundary';
import { startVersionWatcher } from './utils/versionWatcher';
// İt333 (2026-06-08) — kalıcı global cache invalidation zırhı.
import { attachCacheBusters, clearStaleCaches } from './utils/cacheInvalidation';
import { useTranslation } from 'react-i18next';
import { getDirection } from './i18n';
// Eager: Login ve Layout (ilk yükleme için gerekli)
import Login from './pages/Login';
import Layout from './components/Layout';
import AccessDenied from './pages/AccessDenied';
import ExportNotification from './components/ExportNotification';
// İt269 (2026-05-23) — Karargah onaylı GLOBAL TooltipProvider.
// Reports/DotStepper/PickingOrders Radix shadcn Tooltip kullanıyor; Provider olmadan
// "Tooltip must be used within TooltipProvider" exception fırlatıyordu (Reports beyaz ekran).
// App seviyesinde tek Provider tüm sayfaları kapsar.
import { TooltipProvider } from './components/ui/tooltip';

// İt258 (2026-05-22) — Karargah talimatı: SPA 404 sayfası dino.html'e redirect.
// useEffect mount'ta native nav (window.location.replace) → OLS /dino.html servis eder.
// React Router catch-all route bu component'i render edince anında dino açılır.
function DinoRedirect() {
  useEffect(() => {
    try { window.location.replace('/dino.html'); } catch (_) { window.location.href = '/dino.html'; }
  }, []);
  return (
    <div className="flex items-center justify-center h-screen bg-slate-900 text-slate-200">
      <p className="text-sm">Yönlendiriliyor…</p>
    </div>
  );
}
// Operasyon Faz 4.12 (2026-05-18): Bekoo AI Karargah talimatı ile pasifleştirildi.
// Backend /api/ai/* 503 dönüyor; frontend bileşeni de UI'dan kaldırıldı.
// Reaktive: bu import + render'ı geri aç + WMS_BEKOO_ENABLED=1 env set et.
// import BekooChat from './components/bekoo/BekooChat';
// Lazy: Tüm sayfa component'leri — code splitting ile ayrı chunk'lara bölünür
const Dashboard = lazy(() => import('./pages/Dashboard'));
const InboundReceipts = lazy(() => import('./pages/InboundReceipts'));
const InboundReceiptDetail = lazy(() => import('./pages/InboundReceiptDetail'));
const PutawayTasks = lazy(() => import('./pages/PutawayTasks'));
const Changelog = lazy(() => import('./pages/Changelog')); // İt410
const PickingHistory = lazy(() => import('./pages/PickingHistory'));
const PickingModule = lazy(() => import('./pages/PickingModule'));
const PickingExecution = lazy(() => import('./pages/PickingExecution'));
const SinglePickingExecution = lazy(() => import('./pages/SinglePickingExecution'));
const BatchPickingExecution = lazy(() => import('./pages/BatchPickingExecution'));
const SingleTaskList = lazy(() => import('./pages/single/SingleTaskList'));
const SingleOrderSelection = lazy(() => import('./pages/single/SingleOrderSelection'));
const SingleCartSelection = lazy(() => import('./pages/single/SingleCartSelection'));
const SingleExecution = lazy(() => import('./pages/single/SingleExecution'));
const MultiTaskList = lazy(() => import('./pages/multi/MultiTaskList'));
const MultiExecution = lazy(() => import('./pages/multi/MultiExecution'));
const BenchTaskList = lazy(() => import('./pages/bench/BenchTaskList'));
const BenchExecution = lazy(() => import('./pages/bench/BenchExecution'));
const StockMovementsReport = lazy(() => import('./pages/reports/StockMovementsReport'));
const Clients = lazy(() => import('./pages/Clients'));
const Stores = lazy(() => import('./pages/Stores'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Users = lazy(() => import('./pages/Users'));
const Packing = lazy(() => import('./pages/Packing'));
const ShelfPicking = lazy(() => import('./pages/shelf/ShelfPicking'));
const ShelfMultiPicking = lazy(() => import('./pages/shelf/ShelfMultiPicking'));
const ShelfPacking = lazy(() => import('./pages/ShelfPacking'));
const Putback = lazy(() => import('./pages/Putback'));
const Shipping = lazy(() => import('./pages/Shipping'));
const OrdersList = lazy(() => import('./pages/OrdersList'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Locations = lazy(() => import('./pages/Locations'));
const LocationDetail = lazy(() => import('./pages/LocationDetail'));
const Carts = lazy(() => import('./pages/Carts'));
const Counting = lazy(() => import('./pages/Counting'));
const CountingByLocation = lazy(() => import('./pages/CountingByLocation'));
const LocationCountingExecution = lazy(() => import('./pages/LocationCountingExecution'));
const CountingByProduct = lazy(() => import('./pages/CountingByProduct'));
const ProductCountingExecution = lazy(() => import('./pages/ProductCountingExecution'));
const CountingByClient = lazy(() => import('./pages/CountingByClient'));
const ClientCountingExecution = lazy(() => import('./pages/ClientCountingExecution'));
const CountingByWarehouse = lazy(() => import('./pages/CountingByWarehouse'));
const WarehouseCountingExecution = lazy(() => import('./pages/WarehouseCountingExecution'));
const Reports = lazy(() => import('./pages/Reports'));
const Charging = lazy(() => import('./pages/Charging'));
const ChargingDetail = lazy(() => import('./pages/ChargingDetail'));
const RolesPermissions = lazy(() => import('./pages/RolesPermissions'));
const BlockedOrders = lazy(() => import('./pages/BlockedOrders'));
const Kule = lazy(() => import('./pages/Kule'));
const ShippingHandover = lazy(() => import('./pages/ShippingHandover'));
const CreateOrder = lazy(() => import('./pages/CreateOrder'));
const Profile = lazy(() => import('./pages/Profile'));
const Settings = lazy(() => import('./pages/Settings'));
const DeveloperLog = lazy(() => import('./pages/DeveloperLog'));
const ApiHealth = lazy(() => import('./pages/ApiHealth')); // It548 Developer-only
const DeveloperPermissions = lazy(() => import('./pages/DeveloperPermissions'));
// İt356 (2026-06-08) — Karargah onaylı BG Metrics Dashboard.
const CargoBgMetrics = lazy(() => import('./pages/CargoBgMetrics'));
// İt363 (2026-06-08) — Karargah onaylı Watchdog Metrics Dashboard.
const WatchdogMetrics = lazy(() => import('./pages/WatchdogMetrics'));
const DepotTransfer = lazy(() => import('./pages/DepotTransfer'));
const CargoCompanies = lazy(() => import('./pages/CargoCompanies'));
const VasDefinitions = lazy(() => import('./pages/VasDefinitions'));
const ConsumableInventory = lazy(() => import('./pages/ConsumableInventory'));
const ConsumableReceipts = lazy(() => import('./pages/ConsumableReceipts'));
const CameraManagement = lazy(() => import('./pages/CameraManagement'));
const DamageReport = lazy(() => import('./pages/DamageReport'));
const PickingProblems = lazy(() => import('./pages/PickingProblems'));
const Support = lazy(() => import('./pages/Support'));
const Leaderboard = lazy(() => import('./pages/Leaderboard'));
const CompetitionReports = lazy(() => import('./pages/CompetitionReports'));
const ReturnProcessing = lazy(() => import('./pages/ReturnProcessing'));
const ReturnToShelf = lazy(() => import('./pages/ReturnToShelf'));
const NfcTest = lazy(() => import('./pages/NfcTest')); // It536 gecici test sayfasi
const GenericPhotoUpload = lazy(() => import('./pages/GenericPhotoUpload'));
const CancellationsCenter = lazy(() => import('./pages/CancellationsCenter'));
const PDKSManagement = lazy(() => import('./pages/PDKSManagement'));
const ItemLookup = lazy(() => import('./pages/ItemLookup'));
const LocationLookup = lazy(() => import('./pages/LocationLookup'));
const InventoryTransfer = lazy(() => import('./pages/InventoryTransfer'));

// Setup axios interceptor for auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// İt327 (2026-06-03) Sidebar↔Content sayım senkronizasyonu — başarılı mutating
// request'lerden sonra sidebar'ın bayatladığını yayınla. Layout dinler ve
// /api/dashboard/stats'i taze çeker. URL eşleşmesi: taskCounts'ı etkileyebilecek
// modül path'leri.
const STALE_PATH_RE = /\/api\/(orders|inbound|putaway|picking|packing|shipping|receipts|carts|inventory|return-receipts|cancellations)/i;
const STALE_METHODS = new Set(['post', 'put', 'patch', 'delete']);
axios.interceptors.response.use(
  (response) => {
    try {
      const cfg = response.config || {};
      const method = String(cfg.method || '').toLowerCase();
      const url = String(cfg.url || '');
      if (STALE_METHODS.has(method) && STALE_PATH_RE.test(url) && response.status >= 200 && response.status < 300) {
        window.dispatchEvent(new CustomEvent('wms:counts-stale', { detail: { url, method } }));
      }
    } catch (e) {
      // Interceptor sessiz — UI akışını bozma
    }
    return response;
  },
  // İt409 (2026-06-09) — Karargah KÜRESEL retry zırhı:
  // Backend WRITE_CONFLICT_RETRY_EXHAUSTED (HTTP 409) dönerse arka planda
  // retry_after_ms (default 1500ms) bekleyip aynı isteği 2 kez sessizce tekrar
  // dener. Saha operatörü 'WriteConflict' raw hatasını ASLA görmeyecek.
  // Sadece transient retry hatasında devreye girer (diğer 409'lar normal akışa düşer).
  async (error) => {
    try {
      const cfg = error.config || {};
      const resp = error.response;
      const code = resp?.data?.detail?.error || resp?.data?.error;
      const isTransientConflict = (
        resp && resp.status === 409 &&
        code === 'WRITE_CONFLICT_RETRY_EXHAUSTED'
      );
      if (isTransientConflict) {
        cfg._it409_retry_count = (cfg._it409_retry_count || 0) + 1;
        if (cfg._it409_retry_count <= 2) {
          const retryAfterMs = (
            Number(resp.data?.detail?.retry_after_ms) ||
            Number(resp.data?.retry_after_ms) ||
            1500
          );
          // eslint-disable-next-line no-console
          console.info(
            `[İt409/CONFLICT-RETRY] attempt=${cfg._it409_retry_count}/2 ` +
            `url=${cfg.url} delay=${retryAfterMs}ms`
          );
          await new Promise((r) => setTimeout(r, retryAfterMs));
          return axios.request(cfg);
        }
      }
    } catch (e) {
      // Interceptor sessiz — fallback reject
    }
    return Promise.reject(error);
  }
);

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="spinner" />
    </div>;
  }
  
  return user ? children : <Navigate to="/login" replace />;
}

function RoleProtectedRoute({ children, allowedRoles, deniedRoles, module }) {
  const { user, loading, canAccessModule, hasRole } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center h-screen">
      <div className="spinner" />
    </div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check module access if module is specified
  if (module && !canAccessModule(module)) {
    return (
      <Layout>
        <AccessDenied
          message="Bu sayfaya erişim yetkiniz yok"
          requiredRole={allowedRoles ? allowedRoles.join(', ') : 'Belirli Roller'}
        />
      </Layout>
    );
  }

  // FIX 2026-05-13: Denied roles (engellenenler) — örn. Client'ı Ürün Görüntüleme'den uzak tut
  if (deniedRoles && deniedRoles.includes(user.role)) {
    return (
      <Layout>
        <AccessDenied
          message="Bu sayfa rolünüze kapalı"
          requiredRole={`${user.role} dışı`}
        />
      </Layout>
    );
  }

  // Check specific roles if allowedRoles is specified
  if (allowedRoles && !hasRole(...allowedRoles)) {
    return (
      <Layout>
        <AccessDenied
          message="Bu işlem için yetkiniz yok"
          requiredRole={allowedRoles.join(', ')}
        />
      </Layout>
    );
  }

  return children;
}

function AppWithToast() {
  const { toast, toasts } = useToast();
  const { i18n } = useTranslation();

  // Update HTML dir attribute when language changes
  useEffect(() => {
    document.documentElement.setAttribute('dir', getDirection(i18n.language));
    document.documentElement.setAttribute('lang', i18n.language);
  }, [i18n.language]);

  // Online/offline connection listener
  useEffect(() => {
    const handleOffline = () => {
      toast({ type: 'error', message: 'İnternet bağlantısı kesildi!', duration: 10000 });
    };
    const handleOnline = () => {
      toast({ type: 'success', message: 'İnternet bağlantısı yeniden sağlandı.', duration: 5000 });
    };
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
    // İt236 (2026-05-22): toast deps'ten çıkarıldı — useToast() her render'da yeni
    // referans, listener register/cleanup mount/unmount'ta yapılmalı (mayın temizliği).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* İt269 — Global TooltipProvider, tüm Radix Tooltip kullanan bileşenleri kapsar */}
      <TooltipProvider delayDuration={200}>
      <Router>
        <Suspense fallback={
          <div className="flex items-center justify-center h-screen">
            <div className="spinner" />
          </div>
        }>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/upload/:token" element={<GenericPhotoUpload />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/nfc-test" element={
            <ProtectedRoute>
              <Layout>
                <NfcTest />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/inbound" element={
            <RoleProtectedRoute module="inbound">
              <Layout>
                <InboundReceipts />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/inbound/completed" element={
            <RoleProtectedRoute module="inbound">
              <Layout>
                <InboundReceipts completedView={true} />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/inbound/:id" element={
            <RoleProtectedRoute module="inbound">
              <Layout>
                <InboundReceiptDetail />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/putaway" element={
            <RoleProtectedRoute module="putaway">
              <Layout>
                <PutawayTasks />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Changelog rol kilitli (Developer/SuperAdmin); ProtectedRoute auth-only,
              sayfanın kendisi rol gate yapar (diğerlerine Lock görünür). */}
          <Route path="/changelog" element={
            <ProtectedRoute>
              <Layout>
                <Changelog />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/putaway/:taskId" element={
            <RoleProtectedRoute module="putaway">
              <Layout>
                <PutawayTasks />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <PickingModule />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Picking History */}
          <Route path="/picking/history" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <PickingHistory />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Single Picking Routes */}
          <Route path="/picking/single" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <SingleTaskList />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking/single/new/select-order" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <SingleOrderSelection />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking/single/new/select-cart" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <SingleCartSelection />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Legacy route */}
          <Route path="/picking/single/select-order" element={
            <Navigate to="/picking/single" replace />
          } />
          <Route path="/picking/single/:taskId" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <SingleExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Multi Picking Routes */}
          <Route path="/picking/multi" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <MultiTaskList />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking/multi/:taskId" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <MultiExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Bench Picking Routes */}
          <Route path="/picking/bench" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <BenchTaskList />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking/bench/:taskId" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <BenchExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Raf Bazlı Toplama (shelf / column-based) — 2026-07-20 */}
          <Route path="/picking/shelf" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <ShelfPicking />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Çoklu Raf Toplama — Bölüm 46 (2026-07-21) */}
          <Route path="/picking/shelf-multi" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <ShelfMultiPicking />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Reports */}
          <Route path="/reports/stock-movements" element={
            <RoleProtectedRoute module="reports">
              <Layout><StockMovementsReport /></Layout>
            </RoleProtectedRoute>
          } />
          {/* Batch Picking */}
          <Route path="/picking/batch/:batchId" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <BatchPickingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking/task/:taskId" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <SinglePickingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking/execute" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <PickingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/packing" element={
            <RoleProtectedRoute module="packing">
              <Layout>
                <Packing />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* Raf Bazlı Paketleme — 2026-07-20 (statik: /packing/:orderId'den önce eşleşir) */}
          <Route path="/packing/shelf" element={
            <RoleProtectedRoute module="packing">
              <Layout>
                <ShelfPacking />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/packing/:orderId" element={
            <RoleProtectedRoute module="packing">
              <Layout>
                <Packing />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/packing/task/:taskId" element={
            <RoleProtectedRoute module="packing">
              <Layout>
                <Packing />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt329 (2026-06-03) ROLE_MODULES senkronizasyonu — allowedRoles tıraşlama
              sonrası ROLE_MODULES'ın gerisinde kaldı (Receiver/Returner/Counter
              putback'tan çıkarıldı). module='putback' tek kanonik kaynak. */}
          <Route path="/putback" element={
            <RoleProtectedRoute module="putback">
              <Layout>
                <Putback />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/shipping" element={
            <RoleProtectedRoute module="shipping">
              <Layout>
                <Shipping />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/orders" element={
            <RoleProtectedRoute module="orders">
              <Layout>
                <OrdersList />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/orders/:id" element={
            <RoleProtectedRoute module="orders">
              <Layout>
                <OrderDetail />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/blocked-orders" element={
            <RoleProtectedRoute module="orders">
              <Layout>
                <BlockedOrders />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/kule" element={
            <RoleProtectedRoute module="dashboard">
              <Layout>
                <Kule />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/shipping/handover" element={
            <RoleProtectedRoute module="shipping">
              <Layout>
                <ShippingHandover />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt353 (2026-06-04) — Karargah emriyle hibrit gate kaldırıldı.
              Önceki İt328 KASITLI DAR WHITELIST patternı (Karargah'ın "tutarsızlık
              riski" Topoloji raporu doğrultusunda) söküldü. Returner + saha rolleri
              modüle yetkili oldukları için CreateOrder sayfasına da girebilir. Sipariş
              yaratma finansal yetki kontrolü artık BACKEND tarafında (services.orders.
              edits.create_order_impl + ROLE_MODULES'un orders kapsamı) tek kanonik
              kaynaktan uygulanır. allowedRoles whitelist patterni audit_module_gates
              CI gate'i ile yeni route'larda yasak. */}
          <Route path="/create-order" element={
            <RoleProtectedRoute module="orders">
              <Layout>
                <CreateOrder />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/products" element={
            <RoleProtectedRoute module="products">
              <Layout>
                <Products />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/products/:id" element={
            <RoleProtectedRoute module="products">
              <Layout>
                <ProductDetail />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/locations" element={
            <RoleProtectedRoute module="locations">
              <Layout>
                <Locations />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/locations/:id" element={
            <RoleProtectedRoute module="locations">
              <Layout>
                <LocationDetail />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/carts" element={
            <RoleProtectedRoute module="carts">
              <Layout>
                <Carts />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <Counting />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* New Counting Routes */}
          <Route path="/counting/location" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <CountingByLocation />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/location/:locationId" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <LocationCountingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/product" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <CountingByProduct />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/product/:productId" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <ProductCountingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/client" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <CountingByClient />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/client/:clientId" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <ClientCountingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/warehouse" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <CountingByWarehouse />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/counting/warehouse/execute" element={
            <RoleProtectedRoute module="counting">
              <Layout>
                <WarehouseCountingExecution />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/clients" element={
            <RoleProtectedRoute module="clients">
              <Layout>
                <Clients />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/stores" element={
            <RoleProtectedRoute module="stores">
              <Layout>
                <Stores />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/inventory" element={
            <RoleProtectedRoute module="inventory">
              <Layout>
                <Inventory />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/item-lookup" element={
            <RoleProtectedRoute module="inventory" deniedRoles={['Client']}>
              <Layout>
                <ItemLookup />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt328 (2026-06-03) ROLE_MODULES senkronizasyonu — eski allowedRoles
              listesi Picker/Putter'ı ekliyordu (ROLE_MODULES[inventory]'de YOKLAR,
              modül guard zaten kilitliyordu — ölü kod) ve Client/Counter/Returner'ı
              dışarıda bırakıyordu (kanonik tabloda inventory hakları var, false-negative
              kilit). Çözüm: allowedRoles kaldırıldı, module='inventory' kanonik
              ROLE_MODULES tablosunu tek kaynak olarak uygular. */}
          <Route path="/location-lookup" element={
            <RoleProtectedRoute module="inventory">
              <Layout>
                <LocationLookup />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt328 (2026-06-03) ROLE_MODULES senkronizasyonu — Berk Pelit Receiver
              vakasının kalıntısı dar liste Client/Counter/Returner'ı dışarıda
              bırakıyordu. allowedRoles kaldırıldı; module='inventory' kanonik
              ROLE_MODULES'i tek kaynak olarak uygular. */}
          <Route path="/inventory-transfer" element={
            <RoleProtectedRoute module="inventory">
              <Layout>
                <InventoryTransfer />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/users" element={
            <RoleProtectedRoute module="users">
              <Layout>
                <Users />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <RoleProtectedRoute module="settings">
              <Layout>
                <Settings />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/admin/api-health" element={
            <RoleProtectedRoute module="developer_log">
              <Layout>
                <ApiHealth />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/admin/developer-log" element={
            <RoleProtectedRoute module="developer_log">
              <Layout>
                <DeveloperLog />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/admin/permissions" element={
            <RoleProtectedRoute module="developer_log">
              <Layout>
                <DeveloperPermissions />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt356 (2026-06-08) — Cargo BG Metrics Dashboard (Developer). */}
          <Route path="/admin/cargo-metrics" element={
            <RoleProtectedRoute module="developer_log">
              <Layout>
                <CargoBgMetrics />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt363 (2026-06-08) — Watchdog Metrics Dashboard (Developer). */}
          <Route path="/admin/watchdog-metrics" element={
            <RoleProtectedRoute module="developer_log">
              <Layout>
                <WatchdogMetrics />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt353 (2026-06-04) — Karargah emriyle hibrit gate kaldırıldı.
              Önceki allowedRoles=['Developer'] backend ROLE_MODULES'un Admin +
              SuperAdmin yetkisini kapı arkasından engelliyordu (Berk Pelit patternı).
              module="depot_transfer" tek kanonik yetki kaynağı. */}
          <Route path="/depot-transfer" element={
            <RoleProtectedRoute module="depot_transfer">
              <Layout>
                <DepotTransfer />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/cargo-companies" element={
            <RoleProtectedRoute module="cargo">
              <Layout>
                <CargoCompanies />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/roles" element={
            <RoleProtectedRoute module="roles">
              <Layout>
                <RolesPermissions />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/reports" element={
            <RoleProtectedRoute module="reports">
              <Layout>
                <Reports />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/charging" element={
            <RoleProtectedRoute module="charging">
              <Layout>
                <Charging />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/charging/:id" element={
            <RoleProtectedRoute module="charging">
              <Layout>
                <ChargingDetail />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/vas-definitions" element={
            <RoleProtectedRoute module="vas">
              <Layout>
                <VasDefinitions />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/consumable-inventory" element={
            <RoleProtectedRoute module="vas">
              <Layout>
                <ConsumableInventory />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/consumable-receipts" element={
            <RoleProtectedRoute module="vas">
              <Layout>
                <ConsumableReceipts />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/damage-report" element={
            <RoleProtectedRoute module="damage">
              <Layout>
                <DamageReport />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/picking-problems" element={
            <RoleProtectedRoute module="picking">
              <Layout>
                <PickingProblems />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/leaderboard/:type" element={
            <ProtectedRoute>
              <Leaderboard />
            </ProtectedRoute>
          } />
          <Route path="/competition-reports" element={
            <RoleProtectedRoute module="competition">
              <Layout>
                <CompetitionReports />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/returns" element={
            <RoleProtectedRoute module="returns">
              <Layout>
                <ReturnProcessing />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* İt329 (2026-06-03) ROLE_MODULES senkronizasyonu — module='putback' kanonik */}
          <Route path="/return-to-shelf/:orderId" element={
            <RoleProtectedRoute module="putback">
              <ReturnToShelf />
            </RoleProtectedRoute>
          } />
          {/* İt329 (2026-06-03) ROLE_MODULES senkronizasyonu — module='cancellations' kanonik
              (operasyonel rollerden Receiver/Putter/Picker/Packer/Shipper/Counter
              cancellations'tan tıraşlandı; yönetici/Lead/Controller/Returner hâlâ erişebilir). */}
          <Route path="/cancellations" element={
            <RoleProtectedRoute module="cancellations">
              <Layout>
                <CancellationsCenter />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/support" element={
            <RoleProtectedRoute module="support">
              <Layout>
                <Support />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/camera-management" element={
            <RoleProtectedRoute module="camera">
              <Layout>
                <CameraManagement />
              </Layout>
            </RoleProtectedRoute>
          } />
          <Route path="/pdks" element={
            <RoleProtectedRoute module="pdks">
              <Layout>
                <PDKSManagement />
              </Layout>
            </RoleProtectedRoute>
          } />
          {/* 404 Catch-All — İt258 (2026-05-22): T-Rex moral pususu.
              Karargah talimatı: SPA 404 sayfası da dino.html'e yönlendirilir.
              window.location.replace ile native nav → OLS /dino.html context static dosyayı serve eder. */}
          <Route path="*" element={<DinoRedirect />} />
        </Routes>
        {/* Operasyon Faz 4.12 (2026-05-18): Bekoo AI pasif — render kaldırıldı */}
        {/* <BekooChat /> */}
        </Suspense>
      </Router>
      </TooltipProvider>
      {/* İt269 — TooltipProvider kapanış */}
      <ToastContainer toasts={toasts} onRemove={toast.remove} />
      <ExportNotification />
    </>
  );
}

// FAZ 9.5 (2026-05-19): FeatureFlagProvider en üst seviyede mount edilir, riskli
// UI/backend özellikleri `.env::REACT_APP_FF_*` üzerinden açılıp kapatılır.

// İt333 — Cart/Picking/Packing GET endpointlerinde global no-cache zırhı.
// POST/PUT/DELETE etkilenmez; sadece bayat veri riski yüksek path'ler.
attachCacheBusters(axios, {
  paths: ['/api/carts/', '/api/packing/carts', '/api/picking/tasks', '/api/picking/available-orders'],
  includeBusterParam: true,
});

function App() {
  useEffect(() => {
    // Yeni build tespit + otomatik yenileme (60sn'de bir)
    startVersionWatcher();
    // İt333 — sayfa açılışında localStorage bayat cache temizliği
    clearStaleCaches();
  }, []);
  return (
    <ErrorBoundary>
      <FeatureFlagProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ConfirmProvider>
              <AppWithToast />
            </ConfirmProvider>
          </AuthProvider>
        </QueryClientProvider>
      </FeatureFlagProvider>
    </ErrorBoundary>
  );
}

export default App;
